import React, { useState, useEffect, useRef } from 'react';

const BiometricUnlockScreen = ({ biometryType, onUnlock, onUsePassword, error: externalError }) => {
  const [prompting, setPrompting] = useState(false);
  const mountedRef = useRef(false);

  const doUnlock = async () => {
    if (prompting) return;
    setPrompting(true);
    try {
      await onUnlock();
    } finally {
      setPrompting(false);
    }
  };

  // Auto-prompt once on mount
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      doUnlock();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    doUnlock();
  };

  const getBiometricIcon = () => {
    if (biometryType === 'Face ID') {
      return (
        <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          {/* Corner brackets */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 3H5a2 2 0 00-2 2v2M17 3h2a2 2 0 012 2v2M3 17v2a2 2 0 002 2h2M19 17v2a2 2 0 01-2 2h-2" />
          {/* Eyes */}
          <path strokeLinecap="round" d="M9 8.5v2M15 8.5v2" />
          {/* Nose */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.5v3.5l-1 1" />
          {/* Mouth */}
          <path strokeLinecap="round" d="M8.5 16a4.5 4.5 0 007 0" />
        </svg>
      );
    }
    // Touch ID / Fingerprint
    return (
      <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a48.667 48.667 0 00-1.115 10.29M12 10.5a.75.75 0 01.75.75c0 3.836-.924 7.46-2.562 10.653M12 10.5c0-2.485.879-4.764 2.343-6.543M12 10.5a.75.75 0 00-.75.75 41.58 41.58 0 01-1.567 11.403" />
      </svg>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center z-[9999]">
      <div className="text-center px-8 max-w-sm">
        {/* Logo area */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-1">Finance Forecaster</h1>
          <p className="text-slate-400 text-sm">Welcome back</p>
        </div>

        {/* Biometric icon */}
        <div className="mb-8">
          <button
            onClick={handleRetry}
            className="mx-auto p-6 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            {getBiometricIcon()}
          </button>
          <p className="text-slate-300 mt-4 text-sm">
            {biometryType ? `Tap to use ${biometryType}` : 'Tap to unlock'}
          </p>
        </div>

        {/* Error message */}
        {externalError && externalError !== 'USER_CANCELLED' && (
          <div className="mb-6 text-red-400 text-sm bg-red-900/20 rounded-lg px-4 py-3">
            {externalError === 'LOCKOUT'
              ? 'Biometric sign-in is temporarily locked. Please use your password.'
              : externalError}
          </div>
        )}

        {/* Use password fallback */}
        <button
          onClick={onUsePassword}
          className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
        >
          Sign in with password instead
        </button>
      </div>
    </div>
  );
};

export default BiometricUnlockScreen;
